import { lazy, Suspense, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Clock } from "lucide-react";
import { analytics } from "@/lib/analytics";

const RoomDropzone = lazy(() => import("@/components/RoomDropzone"));

const FloatingRoomButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setHasScrolled(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {hasScrolled && !isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => { setIsOpen(true); analytics.roomButtonClick(); }}
            className="fixed bottom-6 right-6 z-50 group"
            aria-label="Envoyer une photo de votre pièce"
          >
            {/* Ping animation */}
            <span className="absolute inset-0 rounded-full bg-accent/30 animate-ping" />
            {/* Glow */}
            <span className="absolute -inset-1 rounded-full bg-accent/20 blur-md group-hover:bg-accent/30 transition-colors" />
            {/* Button */}
            <span className="relative flex items-center gap-2 px-5 py-3.5 bg-accent text-accent-foreground rounded-full shadow-lg font-body text-sm uppercase tracking-wider hover:shadow-xl transition-shadow">
              <Camera className="h-4 w-4" />
              <span className="hidden sm:inline">3 conseils offerts</span>
              <span className="sm:hidden">3 conseils offerts</span>
            </span>
            {/* Badge */}
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="absolute -top-2 -left-2 flex items-center gap-1 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-body uppercase tracking-wider rounded-full shadow-md whitespace-nowrap"
            >
              <Clock className="h-2.5 w-2.5" />
              1h
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[480px] sm:max-h-[85vh] z-50 bg-background rounded-lg shadow-2xl overflow-y-auto"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between px-6 pt-5 pb-3 bg-background border-b border-border">
                <div>
                  <h3 className="font-heading text-xl">Envoyez votre photo</h3>
                  <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" />
                    Réponse personnalisée sous 1h
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-secondary rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground font-body mb-6">
                  Déposez une photo de l'espace que vous souhaitez réaménager. Charline vous envoie ses premières idées et propositions.
                </p>
                <Suspense
                  fallback={
                    <div className="h-40 rounded-lg bg-secondary/60 animate-pulse" aria-hidden="true" />
                  }
                >
                  <RoomDropzone />
                </Suspense>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingRoomButton;
